/**
 * "Register a domain" for a customer: availability at the registry, the price on the customer's
 * account, the registrant (one of the customer's subjects), name servers, then the order and the
 * registration — the same steps as a customer ordering it themselves.
 *
 * VNNIC free program (zones whose rule has `promo_code` eligible / required): once a registrant is
 * chosen, "Xin mã khuyến mãi VNNIC" asks VNNIC for a code of that domain and registrant — the
 * eligibility check (the code stays on the server; its hint and remaining validity are shown,
 * "Kiểm tra lại" once it has expired) — and "Áp dụng chương trình miễn phí" re-quotes with
 * `registry_promotion` while the check is valid. The registration requests its own new code.
 * Missing registrant details are listed by field.
 *
 * A TLD that asks for more than the registrant's usual details (".us": the connection to the
 * United States) shows its fields under "Thông tin bổ sung theo yêu cầu của đuôi …" once the name
 * is available; what the registrant gave before, or its own data, fills them where the TLD says
 * so, and a refusal shows its message under the field.
 */

import { Globe } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RegistryProblemCallout } from '../registry/readiness';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api, isApiError, type RegistrantProblem } from '../../api';
import type { CustomerSummary } from '../../api/areas/accounts';
import type { AvailabilityResult, RegistrationQuote, VnnicPromotionCode } from '../../api/areas/operator';
import { Callout, Field } from '../../components/blocks';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { ExtraFieldInputs, useTldExtraFields } from '../domains/ExtraFields';
import { RegistrantIncomplete, RegistrantReadiness } from '../domains/RegistrationStop';
import { initialValues, missingKeys, valuesToSend } from '../../lib/extraFields';
import { formatDateTime, formatMoney } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from './shared';

const YEARS = [1, 2, 3, 5, 10];

/** How a `.vn` registrant is verified: eKYC or a digitally signed declaration (bản khai) —
 * the only two methods (owner decision 2026-09-29). */
function RegistrantVerification({ subjectId }: { subjectId: string }) {
  const { t } = useTranslation('declarations');
  return (
    <Callout tone="info" title={t('verify.title')}>
      <p>{t('verify.body')}</p>
      <ul className="mt-1 list-disc pl-5">
        <li>{t('verify.ekyc')}</li>
        <li>{t('verify.declaration')}</li>
      </ul>
      {subjectId ? (
        <Link to={`/subjects/${encodeURIComponent(subjectId)}?tab=declarations`} target="_blank" className="mt-1 inline-block font-medium text-primary underline-offset-4 hover:underline">
          {t('verify.open')}
        </Link>
      ) : null}
    </Callout>
  );
}

/** Picks a customer by name or e-mail. */
export function CustomerPicker({ value, onChange }: { value: CustomerSummary | null; onChange: (c: CustomerSummary | null) => void }) {
  const { t } = useTranslation('operator');
  const [q, setQ] = useState('');
  const list = useApi((signal) => (q.trim().length >= 2 ? api.listCustomers({ q: q.trim(), limit: 10, status: 'ACTIVE' }, { signal }) : Promise.resolve({ items: [], next_after: null })), [q]);
  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
        <span className="min-w-0 truncate">
          <span className="font-medium">{value.display_name}</span> <span className="text-muted-foreground">{value.email_hint}</span>
        </span>
        <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)}>
          {t('common.choose')}
        </Button>
      </div>
    );
  }
  const items = list.status === 'success' ? list.data.items : [];
  return (
    <div className="grid gap-2">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('register.customerSearch')} autoComplete="off" />
      {q.trim().length >= 2 && list.status === 'success' && items.length === 0 ? <p className="text-xs text-muted-foreground">{t('register.customerNone')}</p> : null}
      {items.length > 0 ? (
        <ul className="max-h-48 overflow-y-auto rounded-md border">
          {items.map((c) => (
            <li key={c.id}>
              <button type="button" className="w-full px-3 py-1.5 text-left text-sm hover:bg-muted" onClick={() => onChange(c)}>
                <span className="font-medium">{c.display_name}</span> <span className="text-xs text-muted-foreground">{c.email_hint}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const PROMO_FIELDS = ['legal_name', 'address', 'province', 'ward', 'street', 'identifier', 'birth_date', 'gender', 'establish_date', 'household_code', 'owner_name'] as const;

/** `HH:mm` in the viewer's time zone. */
function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** `m:ss` left until `iso` (0:00 once past). */
function left(iso: string, now: number): string {
  const s = Math.max(0, Math.floor((new Date(iso).getTime() - now) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The current time, updated every second while `on`. */
function useNow(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [on]);
  return now;
}

/**
 * The VNNIC free program for an eligible zone. "Xin mã khuyến mãi VNNIC" asks VNNIC for a code for
 * the chosen registrant: that is the eligibility check. The code is valid for a short time (30
 * minutes); its remaining validity is shown, and once it has expired the button reads "Kiểm tra
 * lại". The program can be applied to the price only while the check is valid. Each registration
 * gets its own new code right before the domain is created.
 */
function VnnicFreeProgram({
  customerId,
  domain,
  subjectId,
  eligibility,
  applied,
  onApply,
}: {
  customerId: string;
  domain: string;
  subjectId: string;
  eligibility: string | null;
  applied: boolean;
  onApply: (apply: boolean) => Promise<void>;
}) {
  const { t } = useTranslation('operator');
  const tr = t as unknown as (key: string, o?: Record<string, unknown>) => string;
  const request = useWrite((b: { customer_id: string; domain: string; subject_id: string }, opts) => api.requestVnnicPromotionCode(b, opts));
  const [issued, setIssued] = useState<VnnicPromotionCode | null>(null);
  const [applying, setApplying] = useState(false);
  const error = request.state.phase === 'error' ? request.state.error : null;
  const missing =
    isApiError(error) && error.code === 'PROMO_REGISTRANT_INCOMPLETE' ? ((error.details as { missing?: string[] } | null)?.missing ?? []) : null;
  const fieldName = (f: string) => (PROMO_FIELDS.some((k) => k === f) ? tr(`register.vnnicPromo.fields.${f}`) : f);
  const code = issued && issued.subject_id === subjectId ? issued : null;
  const now = useNow(Boolean(code));
  const expired = code ? new Date(code.expires_at).getTime() <= now : false;
  const ask = () =>
    void request.run({ customer_id: customerId, domain, subject_id: subjectId }).then((r) => {
      if (r) setIssued(r);
    });
  // VNNIC's own words after our lead-in ("VNNIC did not issue a promotion code: …").
  const vnnicReason = isApiError(error) ? error.message.replace(/^VNNIC did not issue a promotion code:\s*/, '') : '';

  return (
    <Callout tone="info" title={t('register.vnnicPromo.title')}>
      {eligibility ? <p>{eligibility}</p> : null}
      {!subjectId ? (
        <p className="text-muted-foreground">{t('register.vnnicPromo.chooseRegistrant')}</p>
      ) : (
        <div className="mt-1 grid gap-2">
          {code && !expired ? (
            <p>
              {t('register.vnnicPromo.issued', { code: code.code_hint, until: clock(code.expires_at), left: left(code.expires_at, now) })}
              {code.reused ? <span className="ml-1 text-xs text-muted-foreground">{t('register.vnnicPromo.reused')}</span> : null}
            </p>
          ) : code ? (
            <p className="text-muted-foreground">{t('register.vnnicPromo.expired', { code: code.code_hint, until: clock(code.expires_at) })}</p>
          ) : null}
          {!code || expired ? (
            <div>
              <Button type="button" size="sm" variant="outline" disabled={request.busy} onClick={ask}>
                {code ? t('register.vnnicPromo.recheck') : t('register.vnnicPromo.request')}
              </Button>
            </div>
          ) : null}
          {code ? (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={applied}
                // An expired check can still be taken off the price, not put on it.
                disabled={applying || (expired && !applied)}
                onChange={(e) => {
                  setApplying(true);
                  void onApply(e.target.checked).finally(() => setApplying(false));
                }}
              />
              <span>{t('register.vnnicPromo.apply')}</span>
            </label>
          ) : null}
          {code && applied ? <p className="text-xs text-muted-foreground">{t('register.vnnicPromo.applied', { count: code.free_years })}</p> : null}
          {code ? <p className="text-xs text-muted-foreground">{t('register.vnnicPromo.newCodeNote')}</p> : null}
          {missing ? (
            <Callout tone="warn" title={t('register.vnnicPromo.missingTitle')}>
              <p>{t('register.vnnicPromo.missingBody')}</p>
              <ul className="mt-1 list-disc pl-5">
                {missing.map((f) => (
                  <li key={f}>{fieldName(f)}</li>
                ))}
              </ul>
              <Link to={`/subjects/${encodeURIComponent(subjectId)}`} target="_blank" className="mt-1 inline-block font-medium text-primary underline-offset-4 hover:underline">
                {t('register.vnnicPromo.openSubject')}
              </Link>
            </Callout>
          ) : error ? (
            isApiError(error) && error.code === 'PROMO_CODE_REFUSED' ? (
              <Callout tone="warn" title={t('register.vnnicPromo.refused')}>
                <p className="break-words">
                  <span className="font-medium">{t('register.vnnicPromo.refusedReason')}</span> {vnnicReason}
                </p>
              </Callout>
            ) : (
              <WriteError error={error} />
            )
          ) : null}
        </div>
      )}
    </Callout>
  );
}

const KNOWN_REASONS = ['INVALID_NAME', 'TLD_NOT_SUPPORTED', 'REGISTRY_UNAVAILABLE', 'REGISTRY_NOT_CONFIGURED', 'REGISTRY_AUTH_FAILED', 'REGISTRY_ERROR', 'REGISTRY_TIMEOUT', 'TLD_NOT_OFFERED_BY_REGISTRY'] as const;

/** Why a name cannot be registered, in words (the registry's own reason is kept in brackets). */
function useReasonText(): (r: AvailabilityResult) => string {
  const { t } = useTranslation('operator');
  return (r) => {
    const known = KNOWN_REASONS.find((k) => k === r.reason);
    if (known) return t(`register.reasons.${known}`);
    return r.reason ? `${t('register.reasons.taken')} (${r.reason})` : t('register.reasons.taken');
  };
}

function RegisterForm({ close, changed, customer: fixed }: DialogBodyProps & { customer: { id: string; name: string } | null }) {
  const { t, i18n } = useTranslation('operator');
  const { t: tExtra } = useTranslation('domains');
  const reasonText = useReasonText();
  const [picked, setPicked] = useState<CustomerSummary | null>(null);
  const customer = fixed ?? (picked ? { id: picked.id, name: picked.display_name } : null);
  const [domain, setDomain] = useState('');
  const [years, setYears] = useState(1);
  const [promo, setPromo] = useState('');
  const [free, setFree] = useState(false);
  const [availability, setAvailability] = useState<AvailabilityResult | null>(null);
  const [quote, setQuote] = useState<RegistrationQuote | null>(null);
  const [checkError, setCheckError] = useState<unknown>(null);
  const [checking, setChecking] = useState(false);
  const [subject, setSubject] = useState('');
  const [ns, setNs] = useState('');
  const [agreement, setAgreement] = useState('');
  const [reason, setReason] = useState('');
  const [extra, setExtra] = useState<Record<string, string>>({});
  const subjects = useApi(
    (signal) => (customer ? api.listSubjects({ customer_id: customer.id, limit: 100 }, { signal }) : Promise.resolve({ items: [], next_after: null })),
    [customer?.id],
  );
  const order = useWrite((b: Parameters<typeof api.registerDomainForCustomer>[0], opts) => api.registerDomainForCustomer(b, opts));
  const placed = order.state.phase === 'done' ? order.state.result : null;
  // The zone's rule: whether it has the VNNIC free program.
  const zone = availability?.available ? (availability.tld ?? null) : null;
  const rule = useApi((signal) => (zone && (zone === 'vn' || zone.endsWith('.vn')) ? api.getVnZone(zone, { signal }) : Promise.resolve(null)), [zone]);
  const program = rule.status === 'success' && rule.data && rule.data.rule.promo_code !== 'none' ? rule.data.rule.promo : null;
  // The extra data the TLD requires (none for most TLDs), for the chosen registrant.
  const extraSpec = useTldExtraFields(zone, subject);
  // What staff typed over what fills a field for this registrant (an emptied field falls back to
  // it, as the server does).
  const extraValues: Record<string, string> = extraSpec
    ? { ...initialValues(extraSpec.fields), ...Object.fromEntries(Object.entries(extra).filter(([k, v]) => v.trim() && extraSpec.fields.some((f) => f.key === k))) }
    : {};
  const extraMissing = extraSpec ? missingKeys(extraSpec.fields, extraValues) : [];
  const orderError = order.state.phase === 'error' ? order.state.error : null;
  const orderProblems: RegistrantProblem[] =
    isApiError(orderError) && orderError.code === 'REGISTRANT_INCOMPLETE' ? ((orderError.details as { fields?: RegistrantProblem[] } | null)?.fields ?? []) : [];

  const reset = () => {
    setAvailability(null);
    setQuote(null);
    setCheckError(null);
    setFree(false);
    setExtra({});
  };

  /** A new price for the same name (the free program on or off, for the chosen registrant). */
  const requote = async (apply: boolean, subjectId: string) => {
    if (!customer || !availability) return;
    setCheckError(null);
    try {
      setQuote(
        await api.quoteDomainRegistration({
          customer_id: customer.id,
          domain: availability.name,
          years,
          promotion_code: promo.trim() || undefined,
          registry_promotion: apply || undefined,
          subject_id: subjectId || undefined,
        }),
      );
      setFree(apply);
    } catch (e) {
      setCheckError(e);
    }
  };

  const check = async () => {
    if (!customer || !domain.trim()) return;
    setChecking(true);
    reset();
    try {
      const { results } = await api.checkDomainAvailability({ names: [domain.trim()] });
      const r = results[0] ?? null;
      setAvailability(r);
      if (r?.available) {
        setQuote(
          await api.quoteDomainRegistration({
            customer_id: customer.id,
            domain: r.name,
            years,
            promotion_code: promo.trim() || undefined,
            subject_id: subject || undefined,
          }),
        );
      }
    } catch (e) {
      setCheckError(e);
    } finally {
      setChecking(false);
    }
  };

  const hosts = ns.split(/[\s,]+/).map((h) => h.trim()).filter(Boolean);
  const subjectItems = subjects.status === 'success' ? subjects.data.items : [];
  const ready = Boolean(customer && quote && subject && agreement.trim() && reason.trim() && extraMissing.length === 0);

  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!customer || !quote) return;
        void order
          .run({
            customer_id: customer.id,
            quote_id: quote.quote_id,
            subject_id: subject,
            nameservers: hosts,
            accepted_agreement_version: agreement.trim(),
            reason: reason.trim(),
            ...(extraSpec ? { extra_fields: valuesToSend(extraValues) } : {}),
          })
          .then((r) => r && changed());
      }}
    >
      {placed ? (
        <Callout tone="ok">
          {t('register.placed', { name: placed.domain })}{' '}
          <Link to={`/domains/${encodeURIComponent(placed.domain)}`} className="font-medium text-primary underline-offset-4 hover:underline">
            {t('register.openDomain')}
          </Link>
        </Callout>
      ) : (
        <>
          <Field label={t('register.customer')} group>
            {fixed ? <p className="text-sm font-medium">{fixed.name}</p> : <CustomerPicker value={picked} onChange={(c) => { setPicked(c); setSubject(''); reset(); }} />}
          </Field>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
            <Field label={t('register.domain')} hint={t('register.domainHint')}>
              <Input value={domain} onChange={(e) => { setDomain(e.target.value); reset(); }} autoComplete="off" className="font-mono" />
            </Field>
            <Field label={t('register.years')}>
              <select className={selectClass} value={years} onChange={(e) => { setYears(Number(e.target.value)); reset(); }}>
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {t('register.yearsN', { count: y })}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={`${t('register.promotion')} (${t('common.optional')})`}>
              <Input value={promo} onChange={(e) => { setPromo(e.target.value.toUpperCase()); reset(); }} className="w-36 font-mono" />
            </Field>
          </div>
          <div>
            <Button type="button" variant="outline" size="sm" disabled={!customer || !domain.trim() || checking} onClick={() => void check()}>
              {t('register.check')}
            </Button>
          </div>
          {checking ? <Loading rows={1} /> : null}
          {checkError ? <WriteError error={checkError} /> : null}
          {availability ? (
            availability.available === null ? (
              // Why the registry could not answer, with links to the connection and the TLD.
              availability.detail ? <RegistryProblemCallout problem={availability} /> : <Callout tone="warn">{t('register.unknown')}</Callout>
            ) : availability.available ? (
              <Callout tone="ok">{t('register.available', { name: availability.unicode ?? availability.name })}</Callout>
            ) : (
              <Callout tone="warn">{t('register.unavailable', { name: availability.name, reason: reasonText(availability) })}</Callout>
            )
          ) : null}
          {quote?.registry_problem ? <RegistryProblemCallout problem={quote.registry_problem} title={t('register.registryProblem')} /> : null}
          {quote ? (
            <div className="grid gap-2">
              <p className="text-sm">
                {t('register.price', { years: t('register.yearsN', { count: quote.years }), total: formatMoney(quote.total), until: formatDateTime(quote.expires_at) })}
                {quote.discount ? ` ${t('register.discount', { amount: formatMoney({ amount: quote.discount, currency: quote.total.currency }) })}` : null}
              </p>
              {quote.lines?.length ? (
                <ul className="grid gap-1 rounded-md border p-2 text-sm">
                  {quote.lines.map((l, i) => (
                    <li key={`${l.code}-${i}`} className="flex flex-wrap items-baseline justify-between gap-2">
                      <span>
                        {i18n.language === 'vi' ? l.description_vi : l.description_en}
                        {l.kind === 'ADDON' ? <span className="ml-2 text-xs text-muted-foreground">{t('register.addonLine', { tax: l.tax_category === 'KCT' ? 'KCT' : 'VAT' })}</span> : null}
                      </span>
                      <span className="tabular">{formatMoney({ amount: l.total, currency: quote.total.currency })}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          {quote ? (
            <>
              <Field label={t('register.registrant')} hint={t('register.registrantHint')}>
                {subjects.status === 'loading' ? (
                  <Loading rows={1} />
                ) : subjectItems.length === 0 ? (
                  <Callout tone="warn">{t('register.noRegistrant')}</Callout>
                ) : (
                  <select
                    className={selectClass}
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      // The free program is bound to the registrant: back to the usual price.
                      if (free) void requote(false, e.target.value);
                    }}
                    required
                  >
                    <option value="">{t('common.choose')}</option>
                    {subjectItems.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.legal_name ?? s.id}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              {program && customer && availability ? (
                <VnnicFreeProgram
                  key={`${availability.name}|${subject}`}
                  customerId={customer.id}
                  domain={availability.name}
                  subjectId={subject}
                  eligibility={i18n.language === 'vi' ? program.eligibility_vi : program.eligibility_en}
                  applied={free}
                  onApply={(apply) => requote(apply, subject)}
                />
              ) : null}
              {domain.trim().toLowerCase().replace(/\.$/, '').endsWith('.vn') ? <RegistrantVerification subjectId={subject} /> : null}
              <RegistrantReadiness subjectId={subject} domain={availability?.name ?? domain} />
              {extraSpec ? (
                <ExtraFieldInputs spec={extraSpec} values={extraValues} onChange={(k, v) => setExtra((x) => ({ ...x, [k]: v }))} problems={orderProblems} />
              ) : null}
              {extraSpec && extraMissing.length ? (
                <p className="text-xs text-muted-foreground" data-extra-missing>
                  {tExtra('extra.missing', { fields: extraSpec.fields.filter((f) => extraMissing.includes(f.key)).map((f) => f.label).join(', ') })}
                </p>
              ) : null}
              <Field label={t('register.nameservers')} hint={t('register.nameserversHint')}>
                <Textarea value={ns} onChange={(e) => setNs(e.target.value)} rows={3} className="font-mono" />
              </Field>
              <Field label={t('register.agreement')} hint={t('register.agreementHint')}>
                <Input value={agreement} onChange={(e) => setAgreement(e.target.value)} maxLength={64} required />
              </Field>
              <ReasonField value={reason} onChange={setReason} />
            </>
          ) : null}
        </>
      )}
      {order.state.phase === 'error' ? <WriteError error={order.state.error} /> : null}
      {order.state.phase === 'error' ? <RegistrantIncomplete error={order.state.error} hideExtra={Boolean(extraSpec)} /> : null}
      <FormFooter busy={order.busy} disabled={!ready} submit={placed ? undefined : t('register.submit')} onClose={close} />
    </form>
  );
}

/**
 * "Register a domain" button with its dialog. `customer`: fixed (customer page) or picked in the
 * form. `open` / `onOpenChange` / `trigger` / `defaultOpen` as in `ActionDialog`.
 */
export function RegisterDomainButton({
  customer,
  onChanged,
  variant = 'default',
  ...control
}: {
  customer?: { id: string; name: string };
  onChanged?: () => void;
  variant?: 'default' | 'outline';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  trigger?: boolean;
}) {
  const { t } = useTranslation('operator');
  return (
    <ActionDialog label={t('register.button')} icon={Globe} title={t('register.title')} description={t('register.description')} variant={variant} wide onChanged={onChanged} {...control}>
      {(p) => <RegisterForm {...p} customer={customer ?? null} />}
    </ActionDialog>
  );
}
